import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';

import {
  Form,
  Header,
  InternalPage,
  ResetFormHandler,
  Spacer,
  TextField,
} from 'theSource/components';
import {
  CHANGE_PASSWORD_FORM_INITIAL_VALUES,
  CHANGE_PASSWORD_QUERY_KEY,
  getValidationSchema,
  RESET_FORM_ERROR_CODES,
} from './utils';
import { useApiMutation } from 'theSource/utils';
import {
  ChangePasswordErrorDto,
  ChangePasswordFormValues,
  ChangePasswordRequestDto,
  ChangePasswordResponseDto,
} from './types';
import { setAuthenticated } from 'theSource/features';
import { setIsPasswordChanged } from './services';
import { GET_CONTENT_SETTINGS_QUERY_KEY } from 'theSource/queries';
import { ROUTES } from 'theSource/app/routes';
import { ChangePasswordWrapperStyled } from './ChangePasswordStyled';
import { mapChangePasswordData } from './utils/mappingUtils';
import { RequiredLegend } from 'enrollment/features/Shared/RequiredLegend/RequiredLegend';

export const ChangePassword = () => {
  const { t } = useTranslation('pages.changePassword');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoading, mutate: changePassword } = useApiMutation<
    ChangePasswordResponseDto,
    ChangePasswordErrorDto,
    ChangePasswordRequestDto
  >({
    queryKey: CHANGE_PASSWORD_QUERY_KEY,
    method: 'put',
    endpoint: '/password_change',
    queryKeysToInvalidate: GET_CONTENT_SETTINGS_QUERY_KEY,
    onSuccess: () => {
      dispatch(setIsPasswordChanged(true));
      dispatch(setAuthenticated(true));
      navigate(ROUTES.main.home.path);
    },
    getErrorMessage: (error) => error.response?.data.message,
    showSuccessSnackbar: false,
    snackbarOptions: {
      position: 'bottom',
    },
  });

  const handleSubmit = (
    values: ChangePasswordFormValues,
    resetForm: ResetFormHandler
  ) => {
    changePassword(mapChangePasswordData(values), {
      onError: (error) => {
        const errorCode = error.response?.data.error_code;
        if (errorCode && RESET_FORM_ERROR_CODES.includes(errorCode)) {
          resetForm();
        }
      },
    });
  };

  return (
    <Box data-testid="change-password-page">
      <Header hideDrawer />
      <ChangePasswordWrapperStyled>
        <InternalPage title={t('title')}>
          <div className="mt-2">
            <RequiredLegend />
          </div>
          <Form<ChangePasswordFormValues>
            validationSchema={getValidationSchema()}
            onSubmitClick={handleSubmit}
            initialValues={CHANGE_PASSWORD_FORM_INITIAL_VALUES}
            isLoading={isLoading}
            submitButtonLabel={t('submitButtonLabel')}
            submitButtonUseFullWidth
          >
            <TextField
              required
              label={t('oldPassword')}
              type="password"
              id="oldPassword"
              testId="oldPassword"
              name="oldPassword"
            />
            <TextField
              required
              label={t('newPassword')}
              type="password"
              id="newPassword"
              testId="newPassword"
              name="newPassword"
            />
            <TextField
              required
              label={t('reEnterPassword')}
              type="password"
              id="reEnterPassword"
              testId="reEnterPassword"
              name="reEnterPassword"
            />
            <Spacer />
          </Form>
        </InternalPage>
      </ChangePasswordWrapperStyled>
    </Box>
  );
};
